"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.setSlotPausesApiPayloadSchema = exports.setSlotPauseInputSchema = exports.assistantSlotPauseSchema = exports.PAUSE_REASON_MAX_LENGTH = void 0;
exports.subtractIntervals = subtractIntervals;
const zod_1 = require("zod");
const pause_type_catalog_1 = require("./pause-type-catalog");
const pauseTimeSchema = zod_1.z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/);
/** Max length of the free-text reason carried by an "Autre" pause. */
exports.PAUSE_REASON_MAX_LENGTH = 200;
const pauseTypeSchema = zod_1.z.enum(pause_type_catalog_1.ASSISTANT_PAUSE_TYPES);
/**
 * A persisted pause on a planned slot, as surfaced by the API (e.g. on the
 * planning grid). `reason` is non-null only for types that require one.
 */
exports.assistantSlotPauseSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    type: pauseTypeSchema,
    reason: zod_1.z.string().nullable(),
    startTime: pauseTimeSchema,
    endTime: pauseTimeSchema,
});
/**
 * One pause in a set-pauses request. The server assigns the id; `reason` is
 * required (non-empty) when the pause type requires one, and ignored otherwise.
 */
exports.setSlotPauseInputSchema = zod_1.z
    .object({
    type: pauseTypeSchema,
    reason: zod_1.z.string().max(exports.PAUSE_REASON_MAX_LENGTH).optional(),
    startTime: pauseTimeSchema,
    endTime: pauseTimeSchema,
})
    .refine((pause) => toMinutes(pause.endTime) > toMinutes(pause.startTime), {
    message: 'endTime must be after startTime',
    path: ['endTime'],
})
    .refine((pause) => !(0, pause_type_catalog_1.pauseTypeRequiresReason)(pause.type) ||
    (pause.reason !== undefined && pause.reason.trim().length > 0), { message: 'reason is required for this pause type', path: ['reason'] });
/** Body of `PUT /assistants/planning/slots/:id/pauses`. */
exports.setSlotPausesApiPayloadSchema = zod_1.z.object({
    pauses: zod_1.z.array(exports.setSlotPauseInputSchema),
});
function toMinutes(time) {
    const [hours = 0, minutes = 0] = time.split(':').map(Number);
    return hours * 60 + minutes;
}
/**
 * Subtracts a set of "cut" intervals (e.g. pauses) from a base interval,
 * returning the remaining sub-intervals in order. Cuts are clamped to the base
 * and may overlap each other (merged via a moving cursor). The single source of
 * truth for "the slot window with its pauses removed" — used by both the core
 * presence service (to sum billable minutes) and the web to split the displayed
 * assistant periods around pauses.
 *
 * @param base - the interval to carve from (minutes since midnight)
 * @param cuts - the intervals to remove
 * @returns the remaining sub-intervals (empty when the base is empty or fully cut)
 */
function subtractIntervals(base, cuts) {
    if (base.end <= base.start)
        return [];
    const clamped = cuts
        .map((cut) => ({
        start: Math.max(cut.start, base.start),
        end: Math.min(cut.end, base.end),
    }))
        .filter((cut) => cut.end > cut.start)
        .sort((a, b) => a.start - b.start);
    const result = [];
    let cursor = base.start;
    for (const cut of clamped) {
        if (cut.start > cursor)
            result.push({ start: cursor, end: cut.start });
        cursor = Math.max(cursor, cut.end);
    }
    if (cursor < base.end)
        result.push({ start: cursor, end: base.end });
    return result;
}
